import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { HeaderService } from '@/app/common/services/header/header.service';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { AuthService } from '@/app/modules/auth/core/service';
import { User } from '@/app/modules/user/core/type';
import { VehicleRevisionService } from '@/app/modules/vehicle-revision/core/service';
import {
  RevisionStatusEnum,
  VehicleRevisionFindAllParams,
  VehicleRevisionListItem,
} from '@/app/modules/vehicle-revision/core/type';
import {
  exportRevisionListPdf,
  loadRevisionPdfLogo,
  RevisionPdfLogo,
} from '../../utils/revision-pdf.util';

@Component({
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzEmptyModule,
    NzNotificationModule,
    NzPaginationModule,
    NzTableModule,
    NzTagModule,
    NzSelectModule,
  ],
})
export class VehicleRevisionListComponent implements OnInit, OnDestroy {
  items: VehicleRevisionListItem[] = [];
  total = 0;
  currentPage = 1;
  currentPageSize = 10;
  searchTerm = '';
  statusFilter: RevisionStatusEnum | 'all' = 'all';
  currentUser: User | null = null;
  pdfLogo: RevisionPdfLogo = { base64: null, width: 0, height: 0 };

  RevisionStatusEnum = RevisionStatusEnum;

  statusFilterOptions = [
    { label: 'Todos os status', value: 'all' },
    { label: 'Pendente', value: RevisionStatusEnum.OVERDUE },
    { label: 'Em dia', value: RevisionStatusEnum.UP_TO_DATE },
    { label: 'Não configurado', value: RevisionStatusEnum.NOT_CONFIGURED },
  ];

  constructor(
    private vehicleRevisionService: VehicleRevisionService,
    private notification: NzNotificationService,
    private loaderService: LoaderService,
    private headerService: HeaderService,
    private router: Router,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.handleSearchRevision();
    this.headerService.registerAction((searchString) => {
      this.searchTerm = searchString || '';
      return this.handleSearchRevision(1, this.currentPageSize, this.searchTerm);
    }, 'Buscar placa ou modelo');

    this.authService.authenticatedUser.subscribe((user) => {
      this.currentUser = user;
    });

    loadRevisionPdfLogo('assets/images/square-logo.png').then(
      (logo) => (this.pdfLogo = logo),
    );
  }

  ngOnDestroy(): void {
    this.headerService.removeAction();
  }

  handleSearchRevision(
    pageIndex: number = this.currentPage,
    pageSize: number = this.currentPageSize,
    searchString?: string,
  ): void {
    this.currentPage = pageIndex;
    this.currentPageSize = pageSize;

    if (searchString !== undefined) {
      this.searchTerm = searchString;
    }

    const params = this.buildFindAllParams(pageIndex, pageSize);

    this.loaderService
      .executeWithLoader(this.vehicleRevisionService.findAll(params))
      .subscribe({
        next: ({ items, total }) => {
          this.items = items;
          this.total = total;
        },
        error: (error: HttpErrorResponse) => {
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao carregar revisões.',
          );
        },
      });
  }

  onPageChange(pageIndex: number): void {
    this.handleSearchRevision(pageIndex, this.currentPageSize);
  }

  onPageSizeChange(pageSize: number): void {
    this.handleSearchRevision(1, pageSize);
  }

  onStatusFilterChange(): void {
    this.handleSearchRevision(1, this.currentPageSize, this.searchTerm);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = 'all';
    this.handleSearchRevision(1, this.currentPageSize, '');
  }

  exportToPdf(): void {
    const params = this.buildFindAllParams(1, this.total || this.currentPageSize);

    this.loaderService
      .executeWithLoader(this.vehicleRevisionService.findAll(params))
      .subscribe({
        next: ({ items }) => {
          if (!items.length) {
            this.notification.warning(
              'Exportar PDF',
              'Nenhum registro para exportar.',
            );
            return;
          }

          const filters: string[] = [];
          if (this.searchTerm.trim()) {
            filters.push(`Busca: ${this.searchTerm.trim()}`);
          }
          if (this.statusFilter !== 'all') {
            filters.push(`Status: ${this.getStatusLabel(this.statusFilter)}`);
          }

          exportRevisionListPdf({
            items,
            generatedBy: this.currentUser?.name || 'Desconhecido',
            filters,
            logo: this.pdfLogo,
            getStatusLabel: (status) => this.getStatusLabel(status),
            getKmRemainingLabel: (item) => this.getKmRemainingLabel(item),
          });
        },
        error: (error: HttpErrorResponse) => {
          this.notification.error(
            `${error.status}`,
            error.error?.message || 'Erro ao exportar revisões.',
          );
        },
      });
  }

  openDetail(item: VehicleRevisionListItem): void {
    this.router.navigate(['/revisions', item.chassis]);
  }

  getStatusLabel(status: RevisionStatusEnum): string {
    switch (status) {
      case RevisionStatusEnum.OVERDUE:
        return 'Pendente';
      case RevisionStatusEnum.NOT_CONFIGURED:
        return 'Não configurado';
      default:
        return 'Em dia';
    }
  }

  getStatusColor(status: RevisionStatusEnum): string {
    switch (status) {
      case RevisionStatusEnum.OVERDUE:
        return 'error';
      case RevisionStatusEnum.NOT_CONFIGURED:
        return 'warning';
      default:
        return 'success';
    }
  }

  getKmRemainingLabel(item: VehicleRevisionListItem): string {
    if (!item.is_configured) return 'Configurar';
    if (item.revision_status === RevisionStatusEnum.OVERDUE) {
      const overdue = item.current_km - (item.next_revision_km ?? 0);
      return `${overdue.toLocaleString('pt-BR')} km atraso`;
    }
    return `${(item.km_until_revision ?? 0).toLocaleString('pt-BR')} km`;
  }

  getKmRemainingClass(item: VehicleRevisionListItem): string {
    if (!item.is_configured) return 'km-pill km-pill--pending';
    if (item.revision_status === RevisionStatusEnum.OVERDUE) {
      return 'km-pill km-pill--overdue';
    }
    return 'km-pill km-pill--ok';
  }

  getRowClass(item: VehicleRevisionListItem): string {
    let base = 'clickable-row';
    if (item.revision_status === RevisionStatusEnum.OVERDUE) {
      base += ' clickable-row--overdue';
    }
    return base;
  }

  private buildFindAllParams(
    pageIndex: number,
    pageSize: number,
  ): VehicleRevisionFindAllParams {
    const params: VehicleRevisionFindAllParams = { pageIndex, pageSize };
    const term = this.searchTerm.trim();

    if (term && term !== 'undefined') {
      params.searchString = term;
    }

    if (this.isValidStatusFilter(this.statusFilter)) {
      params.status = this.statusFilter;
    }

    return params;
  }

  private isValidStatusFilter(
    status: RevisionStatusEnum | 'all',
  ): status is RevisionStatusEnum {
    return (
      status !== 'all' &&
      Object.values(RevisionStatusEnum).includes(status as RevisionStatusEnum)
    );
  }
}
